import { useMemo, useState } from 'react';
import { Users, Briefcase, Fuel, Sparkles, ArrowRight } from 'lucide-react';
import { EC_GREEN } from '@/components/ai/ChatShowcase';
import { GreenHeader, GREEN_BTN } from '@/components/ai/AiSections';
import { openChatWidget } from '@/components/ai/openChatWidget';
import { useKiosk } from '@/components/ai/kioskContext';
import { EUROPCAR_FLEET, FLEET_CATEGORIES, fleetImage } from '@/data/europcarFleet';
import { EUROPCAR_DUBAI_URL } from '@/i18n/navData';

const fmt = (n) => n.toLocaleString('en-US');

function CategoryChips({ active, onChange }) {
  return (
    <div data-testid="fleet-categories" role="tablist" className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 sm:mx-0 sm:flex-wrap sm:px-0 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      {FLEET_CATEGORIES.map((c) => {
        const on = c.id === active;
        return (
          <button key={c.id} type="button" role="tab" aria-selected={on} data-testid={`fleet-tab-${c.id}`} onClick={() => onChange(c.id)}
            className={`min-h-[44px] flex-shrink-0 rounded-full border px-4 py-2 font-heading font-bold text-xs sm:text-sm transition-colors ${on ? 'border-transparent text-white' : 'border-black/10 bg-white text-[#121212] hover:border-[#159A47] hover:text-[#159A47]'}`}
            style={on ? { backgroundColor: EC_GREEN } : undefined}>
            {c.label}
          </button>
        );
      })}
    </div>
  );
}

function Spec({ icon: Icon, children }) {
  return <span className="inline-flex items-center gap-1.5 font-body text-xs text-[#666666]"><Icon className="w-3.5 h-3.5" style={{ color: EC_GREEN }} />{children}</span>;
}

function FleetCard({ car }) {
  return (
    <article data-testid={`fleet-card-${car.slug}`} className="group flex flex-col overflow-hidden rounded-2xl border border-black/[0.06] bg-white transition-shadow hover:shadow-[0_18px_50px_rgba(21,154,71,0.14)]">
      <div className="relative aspect-[3/2] overflow-hidden bg-[#F1F8F3]">
        <img src={fleetImage(car.slug)} alt={`${car.name} available from Europcar Car Rental: Dubai & NE`} loading="lazy" className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]" />
        <span className="absolute left-3 top-3 rounded-full bg-white/95 px-2.5 py-1 font-heading font-bold text-[10px] tracking-[0.12em] uppercase text-[#121212]">Group {car.group}</span>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <p className="font-heading font-bold text-[11px] tracking-[0.12em] uppercase" style={{ color: EC_GREEN }}>{car.type}</p>
        <h3 className="mt-1 font-heading font-bold text-lg text-[#121212] leading-tight">{car.name} <span className="font-body font-normal text-sm text-[#888888]">or similar</span></h3>
        <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
          <Spec icon={Users}>{car.seats} seats</Spec>
          <Spec icon={Briefcase}>{car.bags} large bags</Spec>
          <Spec icon={Fuel}>{car.fuel} · Auto</Spec>
        </div>
        <div className="mt-5 flex items-end justify-between gap-3 border-t border-black/5 pt-4">
          <div>
            <p className="font-body text-[11px] text-[#888888]">from</p>
            <p className="font-heading font-black text-2xl leading-none text-[#121212]">AED {fmt(car.day)}<span className="ms-1 font-body font-normal text-xs text-[#666666]">/ day</span></p>
            <p className="mt-1 font-body text-[11px] text-[#666666]">Monthly AED {fmt(car.month)} · incl. VAT</p>
          </div>
          <button type="button" data-testid={`fleet-ask-${car.slug}`} onClick={() => openChatWidget(`Hi! Is the ${car.name} (or similar) available for my dates, and what is the best rate?`)} className="inline-flex min-h-[44px] items-center gap-1.5 rounded-full px-4 py-2.5 font-heading font-bold text-xs text-white transition-transform hover:-translate-y-0.5" style={{ backgroundColor: EC_GREEN }}>
            <Sparkles className="w-3.5 h-3.5" /> Ask the concierge
          </button>
        </div>
      </div>
    </article>
  );
}

export function FleetShowcase() {
  const kiosk = useKiosk();
  const [cat, setCat] = useState('all');
  const cars = useMemo(() => (cat === 'all' ? EUROPCAR_FLEET : EUROPCAR_FLEET.filter((c) => c.category === cat)), [cat]);
  return (
    <section data-testid="ai-fleet" className="border-y border-black/5 bg-[#FAFAFA] py-16 sm:py-24">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
          <GreenHeader chip="Popular on UAE roads" title="The Europcar fleet the concierge picks from." desc="Every model below is on the Europcar Car Rental: Dubai & NE lot today, from nippy city cars to 7-seaters, weekend-ready 4x4s and prestige SUVs. You always get your chosen model or one very similar." />
          {!kiosk && <a href={`${EUROPCAR_DUBAI_URL}/fleet`} target="_blank" rel="noopener noreferrer" data-testid="fleet-browse-all" className="inline-flex min-h-[44px] items-center gap-2 self-start font-heading font-bold text-sm text-[#121212] hover:text-[#159A47] lg:self-auto">Browse the full fleet <ArrowRight className="w-4 h-4" /></a>}
        </div>
        <div className="mt-8"><CategoryChips active={cat} onChange={setCat} /></div>
        <div data-testid="fleet-grid" className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {cars.map((car) => <FleetCard key={car.slug} car={car} />)}
        </div>
        <p className="mt-6 font-body text-xs text-[#888888]">Indicative rates incl. 5% VAT and CDW insurance · Unlimited UAE mileage on daily and weekly rentals · Confirm the live rate with the concierge or at booking.</p>
        <div className="mt-8 text-center">
          <button type="button" data-testid="fleet-cta" onClick={openChatWidget} className={GREEN_BTN} style={{ backgroundColor: EC_GREEN }}><Sparkles className="w-4 h-4" /> Ask which car fits my trip</button>
        </div>
      </div>
    </section>
  );
}
